import { computed, reactive, readonly } from 'vue'
import { add } from 'date-fns'

import { events$ } from '@shared/services'

import { DateTimeHelper } from '@shared/helpers/DateTime'
import { logError, logInfo } from '@shared/helpers/Logger'

import { SESSION_EOL, SESSION_GROUP, STORAGE_KEY } from '@shared/config/constants'
import { EVENT_SESSION } from '@shared/config/events'
import { ReactivityUtil } from '@shared/utils/Reactivity'
import { CookieUtil } from '@shared/utils/Cookie'
//import { setUserUid } from '@shared/utils/DataDog'

// DATA
const consoleColor = 'color: green'
const store = reactive({
  active: false,
  basketId: null,
})
// Not alphabetically as the store needs to be initialized first
const session = readonly(store)

function sessionStore() {
  function readFromStorage(key = STORAGE_KEY) {
    const item = CookieUtil.read(key)

    let value
    try {
      value = item && JSON.parse(item)
    } catch (e) {
      logError(['Failed to get session from localstorage\n', e, item])
    }

    const hasExpired = (value && value.eol && !DateTimeHelper.isInFuture(new Date(value.eol))) || !value?.basketId

    if (!value || hasExpired) {
      return false
    } else {
      return { ...value }
    }
  }

  function removeFromStorage(key) {
    CookieUtil.delete(key)
  }

  function writeToStorage(key, payload) {
    CookieUtil.set({
      key,
      value: JSON.stringify(payload),
    })
  }

  function removeAllCookies() {
    const appGroups = [SESSION_GROUP.ENTRY, SESSION_GROUP.OFFER, SESSION_GROUP.QUOTE]
    CookieUtil.deleteAppGroupCookies(appGroups, STORAGE_KEY)
  }

  // METHODS
  function clearSession(opts = { silent: false }) {
    if (!store.active) return Promise.reject()

    const basketId = store.basketId

    if (!opts.silent) {
      ReactivityUtil.reAssign(store, { active: false })
    }

    if (basketId) {
      logInfo(['%cCLEARED SESSION', consoleColor, basketId])
    }

    removeFromStorage(STORAGE_KEY)
    return Promise.resolve()
  }

  /**
   * save "session" into localStorage item
   *
   * @param <Object>payload - basketObject
   * @params <Object>options - hot; setOwner; preventCookie;
   *
   */
  function setSession(payload, options = { hot: true }) {
    const { basketId, jwToken, updateDate } = payload
    const currentSession = { basketId, jwToken, updateDate: updateDate?.substring(0, 24) }

    let localSession
    if (!options.preventCookie) {
      localSession = readFromStorage(STORAGE_KEY)
    }

    if (localSession?.hot) {
      currentSession.eol = add(new Date(), SESSION_EOL)
      currentSession.hot = true
    } else {
      if ((!payload.email || !payload.mobile) && options.hot) {
        currentSession.eol = add(new Date(), SESSION_EOL)
      }
      currentSession.hot = options.hot
    }

    /*
    // log basketId to instana
    if (import.meta.env.VITE_DATADOG_ENABLED) {
      setUserUid(basketId)
    }
    */

    currentSession.active = true
    currentSession.jwToken = jwToken
    ReactivityUtil.reAssign(store, currentSession)

    // @note: For some cases, we can't write a cookie (e.g. Entry)
    if (!options.preventCookie) {
      writeToStorage(STORAGE_KEY, currentSession)
    }

    logInfo(['%cSESSION UPDATED', consoleColor, basketId])
    events$.emit(EVENT_SESSION.UPDATE, basketId)
    // TODO: Return store or nothing at all?
    return Promise.resolve(currentSession)
  }

  function getSession() {
    const cookieData = readFromStorage()

    if (cookieData) {
      return cookieData
    } else if (!session.active) {
      return false
    } else {
      return store
    }
  }

  return {
    clearSession,
    getSession,
    isActive: computed(() => session.active),
    removeAllCookies,
    session,
    setSession,
  }
}

export default sessionStore()
